/**
 * About Leynix — Our Story
 * 
 * This page tells the story of why Leynix exists.
 * It's not a feature list. It's the mission behind the platform.
 * 
 * "Leynix wasn't invented in a board room.
 *  It was built while solving real operational problems."
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Brain } from "lucide-react";
import { Button } from "../../components/ui/button";

export default function About() {
  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      {/* Header */}
      <header className="border-b border-[#E2E8F0] bg-white/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#0D1B33] rounded flex items-center justify-center">
              <span className="text-white font-bold text-lg tracking-tight">A</span>
            </div>
            <span className="font-semibold text-[#0D1B33] text-lg tracking-tight">Leynix</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <Link to="/" className="text-[#475569] hover:text-[#0D1B33] transition-colors">Home</Link>
            <Link to="/login" className="text-[#0D1B33] font-medium hover:text-[#176DFF] transition-colors">Sign In</Link>
            <Button 
              className="bg-[#0D1B33] hover:bg-[#0D1B33]/90 text-white rounded px-5"
              data-testid="header-demo-btn"
            >
              Request Demo
            </Button>
          </nav>
        </div>
      </header>

      {/* Back Link */}
      <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0D1B33] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        {/* Why We Built Leynix */}
        <section className="mb-20">
          <p className="text-[#176DFF] font-medium tracking-wide uppercase text-sm mb-6">
            Our Story
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0D1B33] tracking-tight leading-tight mb-10">
            Why We Built Leynix
          </h1>
          
          <div className="space-y-6 text-lg text-[#475569] leading-relaxed">
            <p className="text-2xl text-[#0D1B33] font-medium leading-snug">
              Most organizations don&apos;t struggle because people don&apos;t care.
            </p>
            <p>
              They struggle because information lives in too many places.
            </p>
            <p>
              Scheduling is disconnected from payroll. Documentation is disconnected 
              from operations. Compliance is disconnected from everyday work.
            </p>
            <p>
              Teams spend hours each week copying data between systems, chasing 
              down information, and reconciling records that should already match.
            </p>
            <p>
              We&apos;ve seen talented people—people who genuinely care about the 
              work they do—spend more time fighting their software than serving 
              the people who depend on them.
            </p>
            <p className="text-[#0D1B33] font-medium text-xl">
              We believe organizations deserve one connected platform that helps 
              people work together—not harder.
            </p>
            <p>
              That&apos;s why we built Leynix.
            </p>
          </div>
        </section>

        {/* Brand Philosophy */}
        <section className="mb-20 bg-[#0D1B33] rounded-lg p-10 lg:p-12">
          <div className="text-center">
            <div className="w-14 h-14 bg-[#176DFF]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Brain className="w-7 h-7 text-[#176DFF]" />
            </div>
            <p className="text-[#176DFF] font-medium tracking-wide uppercase text-sm mb-4">
              Our Philosophy
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-6">
              AI Assists.<br />People Decide.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed max-w-xl mx-auto">
              We believe artificial intelligence should amplify human capability, 
              not replace human judgment. Every recommendation in Leynix remains 
              under human review because the best decisions combine AI intelligence 
              with human experience.
            </p>
          </div>
        </section>

        {/* Built Through Real Operations */}
        <section className="mb-20">
          <p className="text-[#176DFF] font-medium tracking-wide uppercase text-sm mb-6">
            Our Approach
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0D1B33] tracking-tight mb-8">
            Built Through Real Operations
          </h2>
          <div className="space-y-6 text-lg text-[#475569] leading-relaxed">
            <p>
              Leynix wasn&apos;t invented in a board room.
            </p>
            <p>
              It was built while solving real operational problems—in real 
              organizations, with real teams, facing real challenges every day.
            </p>
            <p>
              Every workflow, every automation, every AI recommendation is tested 
              in daily operations before becoming part of the platform. We don&apos;t 
              ship features because they sound good. We ship features because 
              they&apos;ve been proven to help organizations operate more effectively.
            </p>
            <p className="text-[#0D1B33] font-medium">
              This approach gives organizations confidence that every capability 
              in Leynix solves a genuine operational challenge—not a theoretical one.
            </p>
          </div>
        </section>

        {/* A Letter from the Founder */}
        <section className="mb-20">
          <div className="bg-[#F8F9FA] rounded-lg border border-[#E2E8F0] p-10 lg:p-12">
            <p className="text-[#176DFF] font-medium tracking-wide uppercase text-sm mb-6">
              A Letter from the Founder
            </p>
            <div className="space-y-6 text-[#475569] leading-relaxed">
              <p>
                Most organizations don&apos;t struggle because their people aren&apos;t capable.
              </p>
              <p>
                They struggle because knowledge lives inside people instead of inside 
                the organization.
              </p>
              <p>
                When someone is unavailable—out sick, on vacation, overwhelmed, or 
                simply in a meeting—questions wait. Workflows stop. Documents become 
                difficult to locate. Decisions are delayed. Departments feel disconnected.
              </p>
              <p>
                The organization slows down. Not because people don&apos;t care, but because 
                the information they need is locked in someone else&apos;s head.
              </p>
              <p>
                I believed there had to be a better way. Not just better software, but a 
                platform that preserves organizational knowledge, connects it across every 
                department, and makes it available whenever it&apos;s needed—so organizations 
                can keep moving forward, no matter who&apos;s in the office.
              </p>
              <p className="text-[#0D1B33] font-medium">
                That&apos;s what Leynix is. Not software that manages operations. A platform 
                that helps organizations operate with clarity, confidence, and connection.
              </p>
              <p className="text-[#0D1B33]">
                — Anessa
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center pt-8 border-t border-[#E2E8F0]">
          <h3 className="text-2xl font-bold text-[#0D1B33] mb-4">
            Ready to see Leynix in action?
          </h3>
          <p className="text-[#475569] mb-8">
            Request a personalized demonstration.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="bg-[#0D1B33] hover:bg-[#0D1B33]/90 text-white rounded px-8 py-6 text-base"
            >
              Request a Demo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Link to="/">
              <Button 
                variant="outline" 
                className="border-[#E2E8F0] text-[#0D1B33] hover:bg-[#F1F5F9] rounded px-8 py-6 text-base"
              >
                Back to Home
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E2E8F0] bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#0D1B33] rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <span className="text-sm text-[#475569]">Leynix</span>
            </div>
            <div className="flex items-center gap-8 text-sm text-[#475569]">
              <Link to="/login" className="hover:text-[#0D1B33] transition-colors">Sign In</Link>
              <Link to="/" className="hover:text-[#0D1B33] transition-colors">Home</Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-[#E2E8F0] text-center text-sm text-[#475569]">
            <p>&copy; {new Date().getFullYear()} Leynix. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
